/**
 * Close Button Component
 *
 * Reusable close button with hover and focus states for dialogs and modals.
 * Features accessibility support and theme-aware styling.
 *
 * @module components/ui/CloseButton
 */

import React, { useState } from 'react';
import { useTheme } from '@/hooks/useTheme';

/**
 * Props for CloseButton component
 */
export interface CloseButtonProps {
  /** Click handler */
  onClick: () => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Accessible label for screen readers */
  ariaLabel?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Close Button Component
 */
export const CloseButton: React.FC<CloseButtonProps> = ({
  onClick,
  disabled = false,
  ariaLabel = 'Close',
  className = ''
}) => {
  const { isDark } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => !disabled && setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      disabled={disabled}
      aria-label={ariaLabel}
      className={className}
      style={{
        padding: '8px',
        borderRadius: '6px',
        border: isDark
          ? `1px solid ${isHovered || isFocused ? '#6b7280' : '#4b5563'}`
          : `1px solid ${isHovered || isFocused ? '#9ca3af' : '#d1d5db'}`,
        backgroundColor: isDark
          ? (isHovered || isFocused ? '#4b5563' : '#374151')
          : (isHovered || isFocused ? '#f9fafb' : 'white'),
        color: isDark ? '#f9fafb' : '#111827',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontSize: '20px',
        lineHeight: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        outline: isFocused ? `2px solid ${isDark ? '#60a5fa' : '#3b82f6'}` : 'none',
        outlineOffset: '2px',
        transform: isHovered && !disabled ? 'scale(1.05)' : 'scale(1)',
        boxShadow: isHovered && !disabled
          ? isDark
            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.1)'
          : 'none',
        width: '36px',
        height: '36px'
      }}
    >
      ✕
    </button>
  );
};

export default CloseButton;